import { Component, OnInit, Injector, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PatientService } from 'app/core/http/services/patient.service';
import { paginationI, paginationInitialState } from 'app/core/interfaces/layout';
import { Patient } from 'app/core/interfaces/patient';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { PractitionerPatientDetailsComponent } from './details/patient-details.component';
import { EncounterService } from 'app/core/http/services/encounter.service';

@Component({
  selector: 'src-patient',
  templateUrl: './patient.component.html',
  styleUrls: ['./patient.component.scss'],
})
export class PractitionerPatientComponent extends BaseComponent implements OnInit {
  public patients: Patient[] = [];
  public encounters: any;
  public filteredPatients: Patient[] = [];
  public title = 'Pacientes';
  public pagination: paginationI = paginationInitialState;
  public patientWaitingPaginated: Patient[] = [];
  private practitionerId: string = '';
  public patientIds: string[] = [];

  public searchControl = new FormControl('');
  public autocompleteSuggestions: string[] = [];
  public showAutocompletePanel: boolean = false;
  public showAutocompleteHint: boolean = false;

  constructor(private injector: Injector, private patientService: PatientService, private encounterService: EncounterService) {
    super(injector);
  }

  ngOnInit(): void {
    this.practitionerId = this.currentUser.user.practitioner_id;
    this.getEncounters();

    
  }

  setupAutocomplete() {
    this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((searchTerm: any) => {
      this.updateAutocompleteSuggestions(searchTerm);
      this.filterPatients(searchTerm);
    });
  }

  updateAutocompleteSuggestions(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.autocompleteSuggestions = [];
      this.showAutocompletePanel = false;
      this.showAutocompleteHint = searchTerm.length > 0 && searchTerm.length < 2;
      return;
    }

    const term = searchTerm.toLowerCase();
    const suggestions = new Set<string>();

    this.patients.forEach((patient) => {
      if (patient.name && patient.name.toLowerCase().includes(term)) {
        suggestions.add(patient.name);
      }
    });

    this.patients.forEach((patient) => {
      if (patient.address?.city && patient.address.city.toLowerCase().includes(term)) {
        suggestions.add(patient.address.city);
      }
    });

    this.patients.forEach((patient) => {
      if (patient.address?.state && patient.address.state.toLowerCase().includes(term)) {
        suggestions.add(patient.address.state);
      }
    });

    this.autocompleteSuggestions = Array.from(suggestions).slice(0, 5);
    this.showAutocompletePanel = this.autocompleteSuggestions.length > 0;
    this.showAutocompleteHint = false;
  }

  selectSuggestion(suggestion: string) {
    this.searchControl.setValue(suggestion, { emitEvent: false });
    this.showAutocompletePanel = false;
    this.filterPatients(suggestion);
  }

  onInputFocus() {
    if (this.searchControl.value && this.searchControl.value.length >= 2) {
      this.updateAutocompleteSuggestions(this.searchControl.value);
    }
  }

  onInputBlur() {
    setTimeout(() => {
      this.showAutocompletePanel = false;
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const inputElement = document.querySelector('input[matInput]');
    const panelElement = document.querySelector('.autocomplete-panel');

    if (inputElement && panelElement) {
      const clickedInsideInput = inputElement.contains(event.target as Node);
      const clickedInsidePanel = panelElement.contains(event.target as Node);

      if (!clickedInsideInput && !clickedInsidePanel) {
        this.showAutocompletePanel = false;
      }
    }
  }

  getPatients(): void {
    const uniqueIds = new Set(
      this.encounters.map((encounter: any) => encounter.subject?.code).filter((code: string | undefined): code is string => !!code)
    );

    uniqueIds.forEach((id: any) => {
      this.patientService.showNew(id).subscribe({
        next: (patient: Patient) => {
          if (!this.patients) {
            this.patients = [];
          }

          if (!this.patients.some((p) => p.patient_id === patient.patient_id)) {
            this.patients.push(patient);

            this.pagination.total = this.patients.length;
            this.paginate({
              total: this.patients.length,
              limit: 10,
              pages: Math.ceil(this.patients.length / 10),
              page: 1,
            });

            this.filteredPatients =  this.patients

            this.setupAutocomplete();
          }
        },
        error: (err) => {
          console.error(`Erro ao buscar paciente com ID ${id}:`, err);
        },
      });
    });
  }

  getEncounters() {
    this.encounterService.listFinishedEncountersByPractitioner(this.practitionerId).subscribe({
      next: (encounters: any) => {
        this.encounters = encounters;
        this.getPatients();
      },
      error: (err) => {},
    });
  }

  filterPatients(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 3) {
      this.filteredPatients = [...this.patients];
      this.paginate(this.pagination);
    } else {
      const term = searchTerm.toLowerCase();

      this.filteredPatients = this.patients.filter((patient) => {
        return (
          patient.name.toLowerCase().includes(term) ||
          (patient.address?.city && patient.address.city.toLowerCase().includes(term)) ||
          (patient.address?.state && patient.address.state.toLowerCase().includes(term))
        );
      });

      this.paginate({
        total: this.filteredPatients.length,
        limit: 10,
        pages: Math.ceil(this.filteredPatients.length / 10),
        page: 1,
      });
    }
  }

  paginate(pagination: paginationI) {
    this.pagination = pagination;
    const startIndex = (this.pagination.page - 1) * this.pagination.limit;
    const endIndex = startIndex + this.pagination.limit;
    this.patientWaitingPaginated = this.filteredPatients.slice(startIndex, endIndex);
  }

  getPatientsPaginated() {
    const startIndex = this.pagination.limit * (this.pagination.page - 1);
    const endIndex = startIndex + this.pagination.limit;
    this.patientWaitingPaginated = this.filteredPatients.slice(startIndex, endIndex);
  }

  openPatientDetailForm(patient: Patient) {
    const data = {
      title: patient.name,
      param: { patient: patient },
    };

    this.dialog.openComponentDialog(PractitionerPatientDetailsComponent, data);
  }

  showPagination() {
    return this.filteredPatients.length > 10;
  }
}
